import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart,
  Calendar,
  Download,
  RefreshCw,
  MessageCircle,
  Users,
  CheckCircle,
  Eye,
  Clock,
  AlertCircle
} from 'lucide-react';
import { whatsappService } from '../../../../services/whatsappService';


const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState({
    from: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0], // Last 30 days
    to: new Date()?.toISOString()?.split('T')?.[0]
  });

  const [previousPeriodAnalytics, setPreviousPeriodAnalytics] = useState(null);

  useEffect(() => {
    loadAnalytics();
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);

      // Current period
      const { data: currentData } = await whatsappService?.getAnalytics({
        from: dateRange?.from,
        to: dateRange?.to
      });
      setAnalytics(currentData);

      // Previous period for comparison
      const daysDiff = Math.ceil((new Date(dateRange.to) - new Date(dateRange.from)) / (1000 * 60 * 60 * 24));
      const previousFrom = new Date(new Date(dateRange.from) - daysDiff * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
      const previousTo = new Date(new Date(dateRange.from) - 1 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];

      const { data: previousData } = await whatsappService?.getAnalytics({
        from: previousFrom,
        to: previousTo
      });
      setPreviousPeriodAnalytics(previousData);

    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshAnalytics = async () => {
    setRefreshing(true);
    await loadAnalytics();
    setRefreshing(false);
  };

  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return { value: 0, isPositive: true };
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change)?.toFixed(1),
      isPositive: change >= 0
    };
  };

  const getDeliveryRate = (data) => {
    if (!data || data?.total_messages === 0) return 0;
    return Math.round((data?.delivered / data?.total_messages) * 100);
  };

  const getReadRate = (data) => {
    if (!data || data?.delivered === 0) return 0;
    return Math.round((data?.read / data?.delivered) * 100);
  };

  const getFailureRate = (data) => {
    if (!data || data?.total_messages === 0) return 0;
    return Math.round((data?.failed / data?.total_messages) * 100);
  };

  // Message type distribution for chart
  const getMessageTypeData = () => {
    if (!analytics?.by_type) return [];

    return Object.entries(analytics?.by_type)?.map(([type, count]) => ({
      name: type?.replace('_', ' ')?.replace(/\b\w/g, l => l?.toUpperCase()),
      value: count,
      percentage: Math.round((count / analytics?.total_messages) * 100)
    }));
  };

  const messageTypeColors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-yellow-500',
    'bg-pink-500',
    'bg-indigo-500'
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-400/30 border-t-blue-400 rounded-full animate-spin"></div>
      </div>
    );
  }

  const deliveryChange = calculateChange(
    getDeliveryRate(analytics),
    getDeliveryRate(previousPeriodAnalytics)
  );

  const totalMessagesChange = calculateChange(
    analytics?.total_messages || 0,
    previousPeriodAnalytics?.total_messages || 0
  );

  const readRateChange = calculateChange(
    getReadRate(analytics),
    getReadRate(previousPeriodAnalytics)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="p-3 rounded-xl bg-purple-500/20 text-purple-400">
            <BarChart3 className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Analytics Dashboard</h2>
            <p className="text-gray-400">WhatsApp messaging performance metrics</p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400">From:</label>
            <input
              type="date"
              value={dateRange?.from}
              onChange={(e) => setDateRange(prev => ({ ...prev, from: e?.target?.value }))}
              className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
          </div>

          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-400">To:</label>
            <input
              type="date"
              value={dateRange?.to}
              onChange={(e) => setDateRange(prev => ({ ...prev, to: e?.target?.value }))}
              className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
          </div>

          <button
            onClick={refreshAnalytics}
            disabled={refreshing}
            className="px-4 py-2 text-gray-400 hover:text-white border border-gray-600 hover:border-gray-500 rounded-xl transition-colors flex items-center space-x-2"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>

          <button
            onClick={() => {/* Export functionality */ }}
            className="px-4 py-2 text-gray-400 hover:text-white border border-gray-600 hover:border-gray-500 rounded-xl transition-colors flex items-center space-x-2"
          >
            <Download className="w-5 h-5" />
            <span>Export</span>
          </button>
        </div>
      </div>
      {/* Key Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-blue-500/20 text-blue-400">
              <MessageCircle className="w-6 h-6" />
            </div>
            <div className={`flex items-center space-x-1 text-sm ${totalMessagesChange?.isPositive ? 'text-green-400' : 'text-red-400'
              }`}>
              {totalMessagesChange?.isPositive ?
                <TrendingUp className="w-4 h-4" /> :
                <TrendingDown className="w-4 h-4" />
              }
              <span>{totalMessagesChange?.value}%</span>
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {analytics?.total_messages || 0}
          </div>
          <div className="text-sm text-gray-400">Total Messages Sent</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-green-500/20 text-green-400">
              <CheckCircle className="w-6 h-6" />
            </div>
            <div className={`flex items-center space-x-1 text-sm ${deliveryChange?.isPositive ? 'text-green-400' : 'text-red-400'
              }`}>
              {deliveryChange?.isPositive ?
                <TrendingUp className="w-4 h-4" /> :
                <TrendingDown className="w-4 h-4" />
              }
              <span>{deliveryChange?.value}%</span>
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {getDeliveryRate(analytics)}%
          </div>
          <div className="text-sm text-gray-400">Delivery Rate</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-purple-500/20 text-purple-400">
              <Eye className="w-6 h-6" />
            </div>
            <div className={`flex items-center space-x-1 text-sm ${readRateChange?.isPositive ? 'text-green-400' : 'text-red-400'
              }`}>
              {readRateChange?.isPositive ?
                <TrendingUp className="w-4 h-4" /> :
                <TrendingDown className="w-4 h-4" />
              }
              <span>{readRateChange?.value}%</span>
            </div>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {getReadRate(analytics)}%
          </div>
          <div className="text-sm text-gray-400">Read Rate</div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-red-500/20 text-red-400">
              <AlertCircle className="w-6 h-6" />
            </div>
            <span className="text-sm text-gray-400">Failure Rate</span>
          </div>
          <div className="text-3xl font-bold text-white mb-1">
            {getFailureRate(analytics)}%
          </div>
          <div className="text-sm text-gray-400">
            {analytics?.failed || 0} failed messages
          </div>
        </motion.div>
      </div>
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Message Status Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center space-x-3 mb-6">
            <PieChart className="w-6 h-6 text-blue-400" />
            <h3 className="text-xl font-semibold text-white">Message Status Distribution</h3>
          </div>

          <div className="space-y-4">
            {[
              { label: 'Delivered', value: analytics?.delivered || 0, color: 'bg-green-500', icon: CheckCircle },
              { label: 'Sent', value: analytics?.sent || 0, color: 'bg-blue-500', icon: MessageCircle },
              { label: 'Read', value: analytics?.read || 0, color: 'bg-purple-500', icon: Eye },
              { label: 'Failed', value: analytics?.failed || 0, color: 'bg-red-500', icon: AlertCircle },
              { label: 'Pending', value: (analytics?.total_messages || 0) - (analytics?.sent || 0) - (analytics?.failed || 0), color: 'bg-yellow-500', icon: Clock }
            ]?.map((item) => {
              const Icon = item?.icon;
              const percentage = analytics?.total_messages ? (item?.value / analytics?.total_messages) * 100 : 0;

              return (
                <div key={item?.label} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${item?.color}`}></div>
                    <Icon className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-300">{item?.label}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-semibold">{item?.value}</div>
                    <div className="text-xs text-gray-400">{percentage?.toFixed(1)}%</div>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Message Type Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
        >
          <div className="flex items-center space-x-3 mb-6">
            <BarChart3 className="w-6 h-6 text-green-400" />
            <h3 className="text-xl font-semibold text-white">Message Types</h3>
          </div>

          <div className="space-y-4">
            {getMessageTypeData()?.map((item, index) => (
              <div key={item?.name} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${messageTypeColors?.[index % messageTypeColors?.length]}`}></div>
                    <span className="text-gray-300">{item?.name}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-white font-semibold">{item?.value}</div>
                    <div className="text-xs text-gray-400">{item?.percentage}%</div>
                  </div>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${messageTypeColors?.[index % messageTypeColors?.length]}`}
                    style={{ width: `${item?.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
      {/* Performance Summary */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20"
      >
        <div className="flex items-center space-x-3 mb-6">
          <TrendingUp className="w-6 h-6 text-purple-400" />
          <h3 className="text-xl font-semibold text-white">Performance Summary</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-green-400 mb-2">
              {analytics?.total_messages ? Math.round((analytics?.delivered / analytics?.total_messages) * 100) : 0}%
            </div>
            <div className="text-gray-300 font-medium">Overall Success Rate</div>
            <div className="text-sm text-gray-500 mt-1">Messages delivered successfully</div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-purple-400 mb-2">
              {analytics?.delivered ? Math.round((analytics?.read / analytics?.delivered) * 100) : 0}%
            </div>
            <div className="text-gray-300 font-medium">Engagement Rate</div>
            <div className="text-sm text-gray-500 mt-1">Delivered messages that were read</div>
          </div>

          <div className="text-center">
            <div className="text-3xl font-bold text-blue-400 mb-2">
              {analytics?.total_messages || 0}
            </div>
            <div className="text-gray-300 font-medium">Total Volume</div>
            <div className="text-sm text-gray-500 mt-1">Messages in selected period</div>
          </div>
        </div>

        {/* Insights */}
        <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
          <h4 className="text-lg font-semibold text-white mb-3">Key Insights</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-300">
                {getDeliveryRate(analytics) >= 90 ? 'Excellent delivery rate indicates good API health' :
                  getDeliveryRate(analytics) >= 70 ? 'Good delivery rate with room for improvement' : 'Delivery rate needs attention - check API configuration'}
              </span>
            </div>
            <div className="flex items-start space-x-2">
              <Eye className="w-4 h-4 text-purple-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-300">
                {getReadRate(analytics) >= 60 ? 'High engagement - messages are being read' :
                  getReadRate(analytics) >= 30 ? 'Moderate engagement - consider optimizing content' : 'Low read rate - review message timing and content'}
              </span>
            </div>
            <div className="flex items-start space-x-2">
              <Users className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-300">
                {analytics?.total_messages > 100 ? 'High volume messaging - monitor rate limits' :
                  analytics?.total_messages > 10 ? 'Moderate usage - scale up gradually' : 'Low volume - consider increasing outreach'}
              </span>
            </div>
            <div className="flex items-start space-x-2">
              <Calendar className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
              <span className="text-gray-300">
                Peak performance times: Weekdays 10 AM - 4 PM typically show better engagement
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Analytics;